import { Package, Trash2, Zap } from "lucide-react";
import PropTypes from "prop-types";
import { Input, Select } from "../../../components/FormComponents";
import { formatCurrency } from "../../../utils/proposalHelpers";

export default function StepItems({
	selectedItems,
	addItem,
	removeItem,
	updateItem,
	plans,
	modules,
	errors,
	totalValue,
}) {
	return (
		<div className="space-y-6">
			{/* Header / Summary */}
			<div className="flex flex-col md:flex-row gap-6">
				<div className="flex-1 bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 relative overflow-hidden">
					<div className="absolute top-0 right-0 p-4 opacity-10">
						<Package className="w-24 h-24 text-white" />
					</div>
					<div className="relative z-10">
						<h2 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">
							Valor Total da Proposta
						</h2>
						<div className="text-4xl font-bold text-white tracking-tight flex items-baseline gap-2">
							<span className="text-2xl text-slate-500">R$</span>
							{formatCurrency(totalValue)}
						</div>
						<p className="text-xs text-slate-500 mt-2">
							{selectedItems.length}{" "}
							{selectedItems.length === 1
								? "item selecionado"
								: "itens selecionados"}
						</p>
					</div>
				</div>

				<div className="flex-1 space-y-4">
					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-4">
						<div className="flex items-center gap-2 mb-3">
							<div className="w-6 h-6 rounded bg-cyan-500/20 flex items-center justify-center">
								<Package className="w-3.5 h-3.5 text-cyan-400" />
							</div>
							<span className="text-sm font-bold text-white">
								Adicionar Planos
							</span>
						</div>
						<Select
							value=""
							onChange={(e) => {
								const plan = plans.find(
									(p) => p.id === parseInt(e.target.value, 10),
								);
								if (plan)
									addItem({ ...plan, item_type: "plan", item_id: plan.id });
							}}
							options={plans.map((p) => ({
								value: p.id,
								label: `${p.name} - R$ ${formatCurrency(p.value)}`,
							}))}
							placeholder="Selecione um plano..."
							className="text-sm"
						/>
					</div>

					<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-4">
						<div className="flex items-center gap-2 mb-3">
							<div className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center">
								<Zap className="w-3.5 h-3.5 text-purple-400" />
							</div>
							<span className="text-sm font-bold text-white">
								Adicionar Módulos
							</span>
						</div>
						<Select
							value=""
							onChange={(e) => {
								const mod = modules.find(
									(m) => m.id === parseInt(e.target.value, 10),
								);
								if (mod)
									addItem({ ...mod, item_type: "module", item_id: mod.id });
							}}
							options={modules.map((m) => ({
								value: m.id,
								label: `${m.name} - R$ ${formatCurrency(m.value)}`,
							}))}
							placeholder="Selecione um modulo..."
							className="text-sm"
						/>
					</div>
				</div>
			</div>

			{errors.items && (
				<div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400 flex items-center gap-2 animate-in slide-in-from-top-2">
					<span className="w-1.5 h-1.5 rounded-full bg-red-500" />
					{errors.items}
				</div>
			)}

			{/* Items List */}
			<div className="space-y-3">
				<h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest pl-1 mb-2">
					Itens Selecionados
				</h3>

				{selectedItems.map((item, idx) => (
					<div
						key={item.id || `selected-item-${idx}`}
						className="group relative bg-slate-900/40 border border-white/5 rounded-xl p-4 transition-all duration-200 hover:border-cyan-500/30 hover:bg-slate-800/60 animate-in fade-in slide-in-from-left-4"
						style={{ animationDelay: `${idx * 50}ms` }}
					>
						<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
							{/* Icon & Type */}
							<div className="flex items-center gap-4 min-w-[180px]">
								<div
									className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${item.item_type === "plan" ? "bg-cyan-500/10 text-cyan-400 shadow-cyan-900/10" : "bg-purple-500/10 text-purple-400 shadow-purple-900/10"}`}
								>
									{item.item_type === "plan" ? (
										<Package className="w-6 h-6" />
									) : (
										<Zap className="w-6 h-6" />
									)}
								</div>
								<div>
									<span
										className={`text-[10px] font-bold uppercase tracking-wider ${item.item_type === "plan" ? "text-cyan-500" : "text-purple-500"}`}
									>
										{item.item_type === "plan" ? "Plano" : "Módulo"}
									</span>
									<Input
										value={item.name}
										onChange={(e) => updateItem(idx, "name", e.target.value)}
										className="bg-transparent border-none p-0 h-auto text-white font-bold text-base focus:ring-0 w-full hover:underline decoration-white/20 underline-offset-4 cursor-pointer"
										aria-label="Nome do item"
									/>
									<Input
										value={item.subtitle || ""}
										onChange={(e) =>
											updateItem(idx, "subtitle", e.target.value)
										}
										className="bg-transparent border-none p-0 h-auto text-slate-500 text-xs focus:ring-0 w-full placeholder-slate-600"
										placeholder="Adicionar subtítulo..."
										aria-label="Subtítulo do item"
									/>
								</div>
							</div>

							{/* Divider for mobile */}
							<div className="h-px w-full bg-white/5 sm:hidden" />

							{/* Inputs Grid */}
							<div className="flex-1 w-full grid grid-cols-2 md:grid-cols-3 gap-4 items-center">
								<div>
									<label
										className="block text-[10px] uppercase font-bold text-slate-600 mb-1"
										htmlFor={`item-value-${idx}`}
									>
										Valor Unit.
									</label>
									<div className="relative mt-1">
										<span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
											R$
										</span>
										<input
											id={`item-value-${idx}`}
											type="number"
											value={item.value}
											onChange={(e) => updateItem(idx, "value", e.target.value)}
											className="w-full bg-slate-950/50 border border-white/5 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-colors"
										/>
									</div>
								</div>
								<div>
									<label
										className="block text-[10px] uppercase font-bold text-slate-600 mb-1"
										htmlFor={`item-qty-${idx}`}
									>
										Quantidade
									</label>
									<input
										id={`item-qty-${idx}`}
										type="number"
										min="1"
										value={item.quantity}
										onChange={(e) =>
											updateItem(idx, "quantity", e.target.value)
										}
										className="w-full bg-slate-950/50 border border-white/5 rounded-lg px-3 py-1.5 text-sm text-center text-white focus:outline-none focus:border-cyan-500/50 transition-colors mt-1"
									/>
								</div>
								<div className="hidden md:block text-right">
									<span className="block text-[10px] uppercase font-bold text-slate-600 mb-1">
										Subtotal
									</span>
									<p className="text-base font-bold text-green-400 mt-2">
										R${" "}
										{formatCurrency(
											parseFloat(item.value || 0) * (item.quantity || 1),
										)}
									</p>
								</div>
							</div>

							{/* Actions */}
							<button
								type="button"
								onClick={() => removeItem(idx)}
								className="absolute top-4 right-4 sm:static sm:p-2 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
								title="Remover item"
								aria-label="Remover item"
							>
								<Trash2 className="w-4 h-4" />
							</button>
						</div>

						{/* Mobile Subtotal */}
						<div className="mt-4 pt-4 border-t border-white/5 flex justify-between items-center sm:hidden">
							<span className="text-xs text-slate-500 font-medium">
								Subtotal
							</span>
							<span className="text-sm font-bold text-green-400">
								R${" "}
								{formatCurrency(
									parseFloat(item.value || 0) * (item.quantity || 1),
								)}
							</span>
						</div>
					</div>
				))}

				{selectedItems.length === 0 && (
					<div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-slate-800 rounded-xl bg-slate-900/20">
						<div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mb-4">
							<Package className="w-8 h-8 text-slate-600" />
						</div>
						<h3 className="text-lg font-bold text-slate-400">
							Nenhum item selecionado
						</h3>
						<p className="text-sm text-slate-500 max-w-xs text-center mt-1">
							Adicione planos ou módulos acima para compor o valor da proposta.
						</p>
					</div>
				)}
			</div>
		</div>
	);
}

StepItems.propTypes = {
	selectedItems: PropTypes.array.isRequired,
	addItem: PropTypes.func.isRequired,
	removeItem: PropTypes.func.isRequired,
	updateItem: PropTypes.func.isRequired,
	plans: PropTypes.array.isRequired,
	modules: PropTypes.array.isRequired,
	errors: PropTypes.object.isRequired,
	totalValue: PropTypes.number.isRequired,
};
